import React, { useMemo } from 'react';
import {
  Box,
  Flex,
  Heading,
  HStack,
  Icon,
  Image,
  SimpleGrid,
  Table,
  Tbody,
  Td,
  Text,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react';
import {
  MdDescription,
  MdInsertDriveFile,
  MdStar,
  MdStarBorder,
} from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import {
  AppCard,
  OutlineButton,
  SortableTh,
  StatusBadge,
} from '../../../../components/ui';
import { useTableSort } from '../../../../hooks/useTableSort';
import { resolveAssetOwner } from '../../../../data/master/assetOwner';
import { vehicleAgeFromReg } from '../../../../data/master/equipmentAssets';
import { formatDobDisplay } from '../../../../utils/personName';
import { formatIndianDateTime } from '../../../../utils/datetime';
import { EquipmentSidePhotosSection, equipmentFileExtBadge } from './EquipmentSidePhotosSection';

const DETAIL_COLUMNS = { base: 1, sm: 2, md: 3, lg: 4, xl: 5, '2xl': 6 };
const DOC_COLUMNS = { base: 1, sm: 2, md: 3, lg: 4 };

function StarRatingDisplay({ value }) {
  const rating = Number(value);
  if (!Number.isFinite(rating) || rating < 1) {
    return (
      <Text as="span" color="text.secondary">
        —
      </Text>
    );
  }
  const filled = Math.min(5, Math.max(0, Math.round(rating)));
  return (
    <HStack spacing={0.5} aria-label={`${filled} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Icon
          key={n}
          as={n <= filled ? MdStar : MdStarBorder}
          boxSize="16px"
          color={n <= filled ? 'brand.500' : 'gray.300'}
        />
      ))}
    </HStack>
  );
}

function DetailRow({ label, value }) {
  return (
    <Box
      p={{ base: 3, xl: 4 }}
      bg="bg.input"
      borderRadius="sm"
      borderWidth="1px"
      borderColor="border.subtle"
    >
      <Text
        fontSize={{ base: '12px', xl: '13px' }}
        color="text.secondary"
        mb={1}
        fontWeight="500"
      >
        {label}
      </Text>
      <Text
        fontSize={{ base: '15px', xl: '16px' }}
        fontWeight="600"
        whiteSpace="pre-wrap"
        lineHeight="1.4"
      >
        {value || '—'}
      </Text>
    </Box>
  );
}

function DocumentCard({ doc, onOpen }) {
  const isImage = doc.kind === 'image' && !!doc.url;
  const isPdf = doc.kind === 'pdf';

  return (
    <Box
      as="button"
      type="button"
      textAlign="left"
      w="100%"
      overflow="hidden"
      bg="bg.input"
      borderRadius="sm"
      borderWidth="1px"
      borderColor="border.subtle"
      cursor="pointer"
      _hover={{ borderColor: 'utkarsh.amber' }}
      onClick={() => onOpen?.(doc)}
      sx={{ '& > *': { pointerEvents: 'none' } }}
    >
      {isImage ? (
        <Box
          w="100%"
          h={{ base: '140px', xl: '160px' }}
          bg="blackAlpha.50"
          overflow="hidden"
        >
          <Image
            src={doc.url}
            alt={doc.label || doc.fileName || 'Document'}
            w="100%"
            h="100%"
            objectFit="cover"
          />
        </Box>
      ) : null}
      <Flex align="center" gap={3} p={{ base: 3, xl: 4 }}>
        {!isImage ? (
          <Flex
            w={{ base: '44px', xl: '52px' }}
            h={{ base: '44px', xl: '52px' }}
            align="center"
            justify="center"
            borderRadius="sm"
            bg={isPdf ? 'whiteAlpha.100' : 'bg.amberTint'}
            flexShrink={0}
          >
            <Icon
              as={isPdf ? MdDescription : MdInsertDriveFile}
              boxSize={{ base: 6, xl: 7 }}
              color="text.secondary"
            />
          </Flex>
        ) : null}
        <VStack align="start" spacing={0} minW={0} flex={1}>
          <Text fontSize={{ base: '14px', xl: '15px' }} fontWeight="600" noOfLines={1}>
            {doc.label}
          </Text>
          <Text
            fontSize={{ base: '12px', xl: '13px' }}
            color="text.secondary"
            noOfLines={1}
            fontFamily="mono"
          >
            {doc.fileName}
          </Text>
        </VStack>
        <StatusBadge status="neutral">{equipmentFileExtBadge(doc)}</StatusBadge>
      </Flex>
    </Box>
  );
}

/** Full equipment detail sections — Villager or Vendor owned. */
export function EquipmentDetailContent({
  asset,
  onManageDocuments,
  onOpenDocument,
  onDocumentsChanged,
  indentHistory = [],
  lookups,
}) {
  const navigate = useNavigate();
  const owner = useMemo(() => resolveAssetOwner(asset, lookups), [asset, lookups]);
  const history = useMemo(
    () => indentHistory || [],
    [indentHistory],
  );
  const {
    sorted: sortedHistory,
    sort,
    toggleSort,
  } = useTableSort(history, {}, { key: 'at', dir: 'desc' });
  const vehicleAge = asset ? vehicleAgeFromReg(asset.dateOfReg) : null;
  const documents = asset?.documents || [];
  const complianceDocs = documents.filter(
    (d) => d.group === 'compliance' || d.group === 'other',
  );

  const openDoc = (doc) => {
    if (onOpenDocument) {
      onOpenDocument(doc);
      return;
    }
  };

  const capacityLabel = asset?.capacity
    ? `${asset.capacity} ${asset.capacityUom || ''}`.trim()
    : '—';

  return (
    <>
      <AppCard mb={6}>
        <Heading size={{ base: 'sm', xl: 'md' }} mb={4}>
          Owner & location
        </Heading>
        <SimpleGrid columns={DETAIL_COLUMNS} spacing={{ base: 3, xl: 4 }}>
          <DetailRow label="Owner type" value={owner.ownerType} />
          <Box
            p={{ base: 3, xl: 4 }}
            bg="bg.input"
            borderRadius="sm"
            borderWidth="1px"
            borderColor="border.subtle"
          >
            <Text
              fontSize={{ base: '12px', xl: '13px' }}
              color="text.secondary"
              mb={1}
              fontWeight="500"
            >
              Owner
            </Text>
            <Text
              fontSize={{ base: '15px', xl: '16px' }}
              fontWeight="600"
              cursor={owner.ownerPath ? 'pointer' : 'default'}
              color={owner.ownerPath ? 'accent.amber' : undefined}
              onClick={() => {
                if (owner.ownerPath) navigate(owner.ownerPath);
              }}
            >
              {owner.ownerName}
            </Text>
            <Text fontSize="12px" color="text.secondary" fontFamily="mono" mt={0.5}>
              {owner.ownerId}
            </Text>
          </Box>
          <DetailRow label="Mobile" value={owner.mobile} />
          <DetailRow label="Village" value={owner.villageName} />
        </SimpleGrid>
      </AppCard>

      <EquipmentSidePhotosSection
        asset={asset}
        documents={documents}
        onOpen={openDoc}
        onChanged={onDocumentsChanged}
      />

      <AppCard mb={6}>
        <Heading size={{ base: 'sm', xl: 'md' }} mb={4}>
          Vehicle basics
        </Heading>
        <SimpleGrid columns={DETAIL_COLUMNS} spacing={{ base: 3, xl: 4 }}>
          <DetailRow label="Equipment description" value={asset.equipmentDescription} />
          <DetailRow label="Equipment type" value={asset.equipmentType} />
          <DetailRow label="Vehicle class" value={asset.vehicleClass} />
          <DetailRow label="Manufacturer" value={asset.manufacturer} />
          <DetailRow label="Model" value={asset.model} />
          <DetailRow label="Variant" value={asset.variant} />
          <DetailRow label="Capacity" value={capacityLabel} />
          <DetailRow label="YOM" value={asset.yom} />
          <DetailRow label="Color" value={asset.color} />
          <DetailRow label="Fuel" value={asset.fuel} />
          <DetailRow label="Reg. No." value={asset.regNo} />
          <DetailRow label="Chassis Number" value={asset.chassisNumber} />
          <DetailRow label="Engine Number" value={asset.engineNumber} />
          <DetailRow label="Date of Reg." value={formatDobDisplay(asset.dateOfReg)} />
          <DetailRow
            label="Vehicle Age"
            value={vehicleAge != null ? `${vehicleAge} years` : '—'}
          />
          <DetailRow label="Reg. Up to" value={formatDobDisplay(asset.regValidUpto)} />
          <DetailRow label="Remarks" value={asset.remarks} />
        </SimpleGrid>
      </AppCard>

      <AppCard mb={6}>
        <Heading size={{ base: 'sm', xl: 'md' }} mb={4}>
          Ownership (RC)
        </Heading>
        <SimpleGrid columns={DETAIL_COLUMNS} spacing={{ base: 3, xl: 4 }}>
          <DetailRow label="Name as per RC" value={asset.nameAsPerRc} />
          <DetailRow label="Son of / Wife of" value={asset.sonOfWifeOf} />
        </SimpleGrid>
      </AppCard>

      <AppCard mb={6}>
        <Heading size={{ base: 'sm', xl: 'md' }} mb={4}>
          Compliance
        </Heading>
        <SimpleGrid columns={DETAIL_COLUMNS} spacing={{ base: 3, xl: 4 }}>
          <DetailRow label="PUC Date" value={formatDobDisplay(asset.pucDate)} />
          <DetailRow label="PUC Valid upto" value={formatDobDisplay(asset.pucValidUpto)} />
          <DetailRow
            label="Insurance issue date"
            value={formatDobDisplay(asset.insuranceIssueDate)}
          />
          <DetailRow
            label="Insurance Valid upto"
            value={formatDobDisplay(asset.insuranceValidUpto)}
          />
          <DetailRow label="Tax Valid upto" value={formatDobDisplay(asset.taxValidUpto)} />
          <DetailRow
            label="Fitness Valid upto"
            value={formatDobDisplay(asset.fitnessValidUpto)}
          />
        </SimpleGrid>
      </AppCard>

      <AppCard mb={6}>
        <Flex justify="space-between" align="start" gap={3} mb={4} wrap="wrap">
          <Box>
            <Heading size={{ base: 'sm', xl: 'md' }} mb={1}>
              Documents attached
            </Heading>
            <Text fontSize={{ base: '13px', xl: '14px' }} color="text.secondary">
              Compliance files (PUC, Insurance, Fitness) and other attachments.
            </Text>
          </Box>
          {onManageDocuments ? (
            <OutlineButton
              size="sm"
              leftIcon={<MdDescription />}
              onClick={() => onManageDocuments('compliance')}
            >
              Manage documents
            </OutlineButton>
          ) : null}
        </Flex>
        {complianceDocs.length > 0 ? (
          <SimpleGrid columns={DOC_COLUMNS} spacing={{ base: 3, xl: 4 }}>
            {complianceDocs.map((doc) => (
              <DocumentCard key={doc.id} doc={doc} onOpen={openDoc} />
            ))}
          </SimpleGrid>
        ) : (
          <Text color="text.secondary" py={4} textAlign="center" fontSize={{ base: '14px', xl: '15px' }}>
            No compliance documents attached yet.
            {onManageDocuments ? ' Use Manage documents to add files.' : ''}
          </Text>
        )}
      </AppCard>

      <AppCard mb={6} p={0} overflow="hidden">
        <Box px={{ base: 5, xl: 6 }} pt={{ base: 5, xl: 6 }} pb={3}>
          <Heading size={{ base: 'sm', xl: 'md' }}>Request / deploy history</Heading>
          <Text fontSize={{ base: '13px', xl: '14px' }} color="text.secondary" mt={1}>
            Where this equipment was recommended or deployed.
          </Text>
        </Box>
        <Box overflowX="auto">
          <Table variant="simple" size="md">
            <Thead bg="bg.input">
              <Tr>
                <SortableTh columnKey="kind" sort={sort} onSort={toggleSort}>
                  Kind
                </SortableTh>
                <SortableTh columnKey="indentId" sort={sort} onSort={toggleSort}>
                  Indent
                </SortableTh>
                <SortableTh columnKey="at" sort={sort} onSort={toggleSort}>
                  When
                </SortableTh>
                <SortableTh columnKey="status" sort={sort} onSort={toggleSort}>
                  Status
                </SortableTh>
                <SortableTh columnKey="rating" sort={sort} onSort={toggleSort}>
                  Rating
                </SortableTh>
                <SortableTh columnKey="note" sort={sort} onSort={toggleSort}>
                  Note
                </SortableTh>
              </Tr>
            </Thead>
            <Tbody>
              {sortedHistory.map((h, idx) => (
                <Tr
                  key={`${h.indentId}-${h.kind}-${idx}`}
                  cursor="pointer"
                  _hover={{ bg: 'bg.input' }}
                  onClick={() => navigate(`/admin/equipments/indents/${h.indentId}`)}
                >
                  <Td>
                    <StatusBadge
                      status={
                        h.kind === 'Deployed'
                          ? 'warning'
                          : h.kind === 'Released'
                            ? 'verified'
                            : 'info'
                      }
                    >
                      {h.kind}
                    </StatusBadge>
                  </Td>
                  <Td fontFamily="mono">{h.indentId}</Td>
                  <Td fontSize="13px">
                    {h.at ? formatIndianDateTime(h.at) || '—' : '—'}
                  </Td>
                  <Td>{h.status}</Td>
                  <Td>
                    <StarRatingDisplay value={h.rating} />
                  </Td>
                  <Td fontSize="13px" color="text.secondary">
                    {h.note || '—'}
                  </Td>
                </Tr>
              ))}
              {history.length === 0 ? (
                <Tr>
                  <Td colSpan={6}>
                    <Text color="text.secondary" py={6} textAlign="center">
                      No recommendations or deployments yet.
                    </Text>
                  </Td>
                </Tr>
              ) : null}
            </Tbody>
          </Table>
        </Box>
      </AppCard>
    </>
  );
}
